import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import AuthLayout, {
  AuthError,
  authButtonClassName,
  authInputClassName,
  authLabelClassName,
  authIconButtonClassName,
} from "../../components/auth/AuthLayout";

const NewPasswordRequired: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeNewPassword } = useAuth();
  const { email, from = "/" } = location.state || {};

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      await completeNewPassword(email, newPassword);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || "Failed to set new password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Set new password"
      subtitle="You need to set a new password to continue"
    >
      <form className="w-full space-y-5" onSubmit={handleSubmit}>
        {error && <AuthError message={error} />}

        <div>
          <label htmlFor="email" className={authLabelClassName}>
            Email
          </label>
          <input
            id="email"
            type="email"
            readOnly
            value={email || ""}
            className={`${authInputClassName} bg-zinc-50 text-zinc-500`}
          />
        </div>

        <div>
          <label htmlFor="newPassword" className={authLabelClassName}>
            New password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              required
              className={`${authInputClassName} pr-12`}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className={authIconButtonClassName}
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? (
                <FaEyeSlash className="h-4 w-4" />
              ) : (
                <FaEye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className={authLabelClassName}>
            Confirm new password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              required
              className={`${authInputClassName} pr-12`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={authIconButtonClassName}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              {showConfirmPassword ? (
                <FaEyeSlash className="h-4 w-4" />
              ) : (
                <FaEye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={authButtonClassName}
        >
          {loading ? "Setting password..." : "Set new password"}
        </button>
      </form>
    </AuthLayout>
  );
};

export default NewPasswordRequired;
